/// <amd-module name="Build/Admin/Common/Components/PoolRoles" />
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { format } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import { TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { ITfsPageData, ITfsPageService } from "Tfs/Platform/Page";

import { Permission } from "Build/Admin/Common/Constants";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";

import * as Resources from "Build/Admin/Common/Resources";

///
// This class wraps the legacy Roles Assignment Control in VSSPreview/Flux/Components/RoleAssignmentControl.
// The properties passed into the LegacyComponent below can be seen in the legacy control with some description.
// A new webplatform based user security control is in progress, so this implementation is needed until then.
// This control passes in a defined resource, which in this case is either a specific TaskAgentQueue or the "all queues"
// resource. It will load the current set of user permissions into the control, and allow for adding new Administrators,
// Users, and Readers of the provided resource.
//
// This control pulls in the legacy web platform which has a loading time and overhead. The implementation ensures it lazy loads only
// when the control is needed, and shows a loading spinner until then.
///

// Copy of IOperationScope from VSS/Identities/Picker/Services for use until this control exists in new web platform
interface IOperationScope {
    Source?: boolean;
    IMS?: boolean;
    AAD?: boolean;
    AD?: boolean;
    WMD?: boolean;
}

const agentQueueRoleScopeId: string = "distributedtask.agentqueuerole";
const globalAgentQueueRoleScopeId: string = "distributedtask.globalagentqueuerole";
const agentPoolRoleScopeId: string = "distributedtask.agentpoolrole";
const globalAgentPoolRoleScopeId: string = "distributedtask.globalagentpoolrole";

const LargeSpinner = () => <Spinner className="pool-security-spinner scroll-hidden" size={SpinnerSize.large} />;

export enum PoolRolesScope {
    Pool,
    Queue
}

export interface IPoolRolesProps {
    selectedRolesScope: PoolRolesScope;
    selectedAgentQueueOrPool?: TaskAgentQueue | TaskAgentPool | undefined;
    hasManagePermission: boolean;
}

export class PoolRoles extends VssComponent<IPoolRolesProps> {
    private _identityService: IVssIdentityService;
    private _tfsPageData: ITfsPageData | undefined;
    private _vssPageData: IPageData | undefined;

    constructor(props: IPoolRolesProps, context: IVssComponentContext) {
        super(props, context);

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        this._tfsPageData = tfsPageService.getData();

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        this._vssPageData = vssPageService.getData();

        this._identityService = this.context.pageContext.getService<IVssIdentityService>("IVssIdentityService");
    }

    public render(): JSX.Element {
        return (
            <div className="pipelines-roles-component flex-grow flex-column">
                <LegacyComponent
                    key={this.props.selectedRolesScope.toString()}
                    loadingComponent={LargeSpinner}
                    modules={["VSSPreview/Flux/Components/RoleAssignmentControl"]}
                    wrappedType="vssRoleAssignmentControl"
                    serviceInstanceId={this._getServiceInstanceId()}
                    userId={this._getUserId()}
                    resourceId={this._getResourceId()}
                    resourceName={this._getResourceName()}
                    scopeId={this._getScopeId()}
                    manageRolesPermission={Permission.Manage}
                    noPermissionsMessage={Resources.ManagePoolRolesNoPermissions}
                    canEdit={this.props.hasManagePermission}
                    canInherit={this.props.selectedAgentQueueOrPool !== undefined}
                    operationScope={this._getOperationScope()}
                    materializeIdentities={true}
                    showAvatars={true}
                    formAvatarUrl={this._getAvatarUrl} />
            </div>
        );
    }

    private _getServiceInstanceId(): string | undefined {
        if (this._vssPageData) {
            return this._vssPageData.serviceInstanceType;
        }
        else {
            return undefined;
        }
    }

    private _getUserId(): string | undefined {
        if (this._vssPageData) {
            return this._vssPageData.user.id;
        }
        else {
            return undefined;
        }
    }

    private _getResourceId(): string {
        const projectDataService = this.context.pageContext.getService<IProjectDataService>("IProjectDataService");

        if (this.props.selectedRolesScope === PoolRolesScope.Queue && projectDataService.hasProjectData()) {
            let queueId: number | undefined = undefined;
            if (this.props.selectedAgentQueueOrPool) {
                queueId = this.props.selectedAgentQueueOrPool.id;
            }

            let projectId: string = "";
            if (this._tfsPageData && this._tfsPageData.project) {
                projectId = this._tfsPageData.project.id;
            }

            return format("{0}_{1}", projectId, queueId);
        }
        else {
            if (this.props.selectedAgentQueueOrPool) {
                return this.props.selectedAgentQueueOrPool.id.toString();
            }
            else {
                return "0";
            }
        }
    }

    private _getResourceName(): string | undefined {
        if (this.props.selectedAgentQueueOrPool) {
            return this.props.selectedAgentQueueOrPool.name;
        }
        else {
            return undefined;
        }
    }

    private _getScopeId(): string {
        if (this.props.selectedRolesScope === PoolRolesScope.Queue) {
            if (this.props.selectedAgentQueueOrPool) {
                return agentQueueRoleScopeId;
            }
            else {
                return globalAgentQueueRoleScopeId;
            }
        }
        else {
            if (this.props.selectedAgentQueueOrPool) {
                return agentPoolRoleScopeId;
            }
            else {
                return globalAgentPoolRoleScopeId;
            }
        }
    }

    private _getAvatarUrl = (id: string): string => {
        return this._identityService.getIdentityImageUrl(id);
    }

    private _getOperationScope(): IOperationScope {
        const operationScope: IOperationScope = {
            IMS: true,
            Source: true
        };

        if (this._vssPageData && !this._vssPageData.isHosted) {
            operationScope.AD = true;
            operationScope.WMD = true;
        }
        else {
            operationScope.AAD = true;
        }

        return operationScope;
    }
}
